"use client";
import { useState, useEffect, Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, useGLTF, Center, Environment, Resize, Sparkles, Stars, Cloud, Clouds, Html } from "@react-three/drei";
import Image from "next/image";
import * as THREE from "three";

// --- FALLING WEATHER EFFECTS ---
function FallingParticles({ type }: { type: "rain" | "snow" }) {
  const count = type === "rain" ? 1500 : 800;
  const meshRef = useRef<any>(null);

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 40;     // X
      pos[i * 3 + 1] = Math.random() * 30;         // Y (start high)
      pos[i * 3 + 2] = (Math.random() - 0.5) * 40; // Z
    }
    return pos;
  }, [count]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;
    const pos = meshRef.current.geometry.attributes.position.array;
    
    for (let i = 0; i < count; i++) {
      // Y axis - falling speed
      const speed = type === "rain" ? (15 + Math.random() * 5) : (2 + Math.random() * 1);
      pos[i * 3 + 1] -= speed * delta;
      
      // X axis - wind/wobble for snow
      if (type === "snow") {
        pos[i * 3] += Math.sin(state.clock.elapsedTime + i) * delta * 1.5;
      }

      // Reset to top if it hits the ground
      if (pos[i * 3 + 1] < -2) {
        pos[i * 3 + 1] = 25 + Math.random() * 5;
        pos[i * 3] = (Math.random() - 0.5) * 40;
      }
    }
    meshRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial 
        size={type === "rain" ? 0.08 : 0.15} 
        color={type === "rain" ? "#7dd3fc" : "#ffffff"} 
        transparent 
        opacity={type === "rain" ? 0.4 : 0.8} 
        sizeAttenuation={true} 
      />
    </points>
  );
}

// --- THUNDERSTORM EFFECTS ---
function Thunderstorm() {
  const flashRef = useRef<any>(null);
  
  useFrame(() => {
    if (!flashRef.current) return;
    // 2% chance every frame to strike lightning
    if (Math.random() > 0.98) {
      flashRef.current.intensity = Math.random() * 8 + 5; // Massive flash
    } else {
      // Fade out the flash quickly
      flashRef.current.intensity = THREE.MathUtils.lerp(flashRef.current.intensity, 0, 0.2);
    }
  });

  return (
    <directionalLight ref={flashRef} position={[0, 20, 0]} color="#e0f2fe" intensity={0} />
  );
}

// --- DYNAMIC WEATHER EFFECTS COMPONENT ---
function WeatherEffects({ code, isDay }: { code: number, isDay: boolean }) {
  const starsRef = useRef<any>(null);
  
  // 🚨 FORCE TEST MODE 🚨
  const FORCE_TEST_MODE = false;

  const isCloudy = FORCE_TEST_MODE || (code >= 1 && code <= 3);
  const isRain = FORCE_TEST_MODE || ((code >= 51 && code <= 67) || (code >= 80 && code <= 82));
  const isSnow = FORCE_TEST_MODE || (code >= 71 && code <= 77);
  const isThunderstorm = FORCE_TEST_MODE || (code >= 95);

  const showSun = FORCE_TEST_MODE || (isDay && !isRain && !isSnow && !isThunderstorm);
  const showMoon = FORCE_TEST_MODE || (!isDay && !isRain && !isSnow && !isThunderstorm);

  useFrame((state) => {
    if (starsRef.current) {
      starsRef.current.rotation.y = state.clock.elapsedTime * 0.02;
    }
  });

  return (
    <group>
      {/* 3D Sun */}
      {showSun && (
        <group position={[-20, 15, -25]}>
          <mesh>
            <sphereGeometry args={[3, 32, 32]} />
            <meshBasicMaterial color={isCloudy ? "#fef08a" : "#fbbf24"} />
          </mesh>
          <mesh>
            <sphereGeometry args={[5, 32, 32]} />
            <meshBasicMaterial color={isCloudy ? "#fef08a" : "#fbbf24"} transparent opacity={0.2} />
          </mesh>
        </group>
      )}

      {/* 3D Moon */}
      {showMoon && (
        <group position={[15, 12, -25]}>
          <mesh>
            <sphereGeometry args={[2, 32, 32]} />
            <meshBasicMaterial color="#e2e8f0" />
          </mesh>
          <mesh>
            <sphereGeometry args={[3.5, 32, 32]} />
            <meshBasicMaterial color="#e2e8f0" transparent opacity={0.15} />
          </mesh>
          <mesh position={[0.7, 0.4, 1.8]} rotation={[0.2, -0.4, 0]}>
            <circleGeometry args={[0.4, 16]} />
            <meshBasicMaterial color="#cbd5e1" />
          </mesh>
        </group>
      )}

      {/* 3D Clouds */}
      {(isCloudy || isRain || isSnow || isThunderstorm) && (
        <Clouds material={THREE.MeshBasicMaterial}>
          <Cloud segments={40} bounds={[20, 4, 20]} volume={15} color={isDay && !isThunderstorm ? (isRain ? "#64748b" : "#f1f5f9") : "#1e293b"} position={[0, 12, -10]} speed={0.2} opacity={0.8} />
          <Cloud segments={20} bounds={[15, 3, 15]} volume={10} color={isDay && !isThunderstorm ? (isRain ? "#475569" : "#ffffff") : "#0f172a"} position={[10, 8, -15]} speed={0.1} opacity={0.9} />
        </Clouds>
      )}
      
      {/* Stars */}
      {(FORCE_TEST_MODE || !isDay) && (
        <Stars ref={starsRef} radius={50} depth={50} count={4000} factor={4} saturation={0} fade />
      )}

      {/* Falling particles */}
      {isRain && <FallingParticles type="rain" />}
      {isSnow && <FallingParticles type="snow" />}
      
      {/* Thunderstorm Lightning */}
      {isThunderstorm && <Thunderstorm />}
    </group>
  );
}

// The new Diorama from Blender
function MashukMountain({ isDay }: { isDay: boolean }) {
  const { scene } = useGLTF('/mashuk_diorama.glb?v=2');
  
  useMemo(() => {
    scene.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        if (child.material) {
          child.material.side = THREE.DoubleSide;

          if (child.material.name === "BaseMaterial") {
            // Premium solid ceramic for the cylinder walls
            child.material = new THREE.MeshPhysicalMaterial({
              color: isDay ? 0xffffff : 0x0f172a, 
              transparent: false,
              opacity: 1,
              metalness: 0.2,
              roughness: 0.2, 
              ior: 1.5,
              thickness: 5.0, 
              envMapIntensity: 2.0,
              side: THREE.DoubleSide,
            });
          } else {
            // Fix the terrain satellite texture so it doesn't shine like plastic (whitish tint)
            child.material.roughness = 1.0;
            child.material.metalness = 0.0;
          }
        }
      }
    });
  }, [scene, isDay]);

  return (
    <group>
      <primitive object={scene} />
      {/* Magical fireflies floating around the mountain */}
      <Sparkles 
        count={150} 
        scale={[15, 6, 15]} // Wide ring around the base, not a sphere, so they don't spawn inside!
        size={3} 
        speed={0.3} 
        opacity={0.8} 
        color={isDay ? "#fbbf24" : "#34d399"} // Amber in day, Emerald at night
        noise={0.5} 
        position={[0, -1, 0]} 
      />
    </group>
  );
}

useGLTF.preload('/mashuk_diorama.glb?v=2');

// Isolate the ticking clock so it doesn't cause the 3D Canvas to re-render every second!
function Clock() {
  const [localTime, setLocalTime] = useState("");
  
  useEffect(() => {
    const updateTime = () => {
      setLocalTime(new Date().toLocaleTimeString("ru-RU", {
        timeZone: "Europe/Moscow",
        hour: "2-digit",
        minute: "2-digit"
      }));
    };
    updateTime(); 
    const timer = setInterval(updateTime, 1000); 
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="flex items-center gap-1.5">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      {localTime}
    </span>
  );
}
// --- POINTS OF INTEREST ---
const POIS = [
  {
    id: 'hotel',
    title: 'Отель "Затерянный Рай"',
    description: 'Наш премиальный эко-отель у подножия горы Машук. Идеальное место для уединения, биохакинга и цифрового детокса.',
    position: new THREE.Vector3(-0.840, 1.919, -1.916),
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'lermontov',
    title: 'Место дуэли Лермонтова',
    description: 'Исторический памятник на месте трагической дуэли великого русского поэта М.Ю. Лермонтова в 1841 году.',
    position: new THREE.Vector3(-1.178, 2.028, -1.295),
    image: 'https://images.unsplash.com/photo-1584347781754-b52e6900257e?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'mashuk',
    title: 'Вершина г. Машук',
    description: 'Панорамная площадка с захватывающим видом на Эльбрус и знаменитая надпись "Пятигорск".',
    position: new THREE.Vector3(-0.063, 2.897, -0.080),
    image: 'https://images.unsplash.com/photo-1581404917879-53e19259f56e?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'city',
    title: 'Город Пятигорск',
    description: 'Исторический центр города-курорта с уникальной архитектурой, парками и лечебными источниками.',
    position: new THREE.Vector3(-1.643, 1.576, 2.587),
    image: 'https://images.unsplash.com/photo-1620182470747-d57662c64db6?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'vorota',
    title: 'Ворота Солнца',
    description: 'Живописная каменная арка на склоне Машука. Одно из лучших мест для встречи рассветов и романтических прогулок.',
    position: new THREE.Vector3(-1.148, 2.091, -0.025),
    image: 'https://images.unsplash.com/photo-1533088924036-7c919d7d49ba?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'proval',
    title: 'Озеро Провал',
    description: 'Уникальное подземное озеро карстового происхождения с невероятно красивой бирюзовой водой сероводородных источников.',
    position: new THREE.Vector3(0.828, 1.937, 0.752),
    image: 'https://images.unsplash.com/photo-1502479532588-466d6d45170d?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'cvetnik',
    title: 'Парк Цветник',
    description: 'Сердце курортной жизни Пятигорска. Лермонтовская галерея, бюветы с минеральной водой и красивейшие клумбы.',
    position: new THREE.Vector3(-0.914, 1.817, 1.119),
    image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4ce82?auto=format&fit=crop&q=80&w=800'
  }
];

export default function Location3D() {
  const [weather, setWeather] = useState({ isDay: false, loading: true, temp: 0, code: 0, time: "" });
  const [activePOI, setActivePOI] = useState<typeof POIS[0] | null>(null);
  useEffect(() => {
    fetch('/api/weather')
      .then(res => res.json())
      .then(data => setWeather({ 
        isDay: data.isDay, 
        loading: false, 
        temp: data.temp, 
        code: data.weatherCode, 
        time: data.time 
      }))
      .catch(err => console.error("Weather fetch failed", err));
  }, []);

  const getWeatherDesc = (code: number) => {
    if (code === 0) return "Ясно";
    if (code >= 1 && code <= 3) return "Облачно";
    if (code >= 45 && code <= 48) return "Туман";
    if (code >= 51 && code <= 67) return "Дождь";
    if (code >= 71 && code <= 77) return "Снег";
    if (code >= 80 && code <= 82) return "Ливень";
    if (code >= 95) return "Гроза";
    return "Ясно";
  };

  const isDay = weather.isDay;
  const isRain = weather.code >= 51;

  // CSS Sky Gradients (Pure sky, no ground!)
  let skyGradient = isDay 
    ? "bg-gradient-to-b from-blue-500 to-sky-200" // Clear day
    : "bg-gradient-to-b from-slate-900 to-indigo-950"; // Night
  
  if (isDay && isRain) {
    skyGradient = "bg-gradient-to-b from-slate-500 to-slate-300"; // Cloudy/Rain day
  }

  return (
    <section id="location" className={`relative min-h-[120vh] w-full overflow-hidden transition-colors duration-1000 ${skyGradient}`}>
      
      {/* Text overlay */}
      <div className="absolute top-12 left-0 w-full z-20 pointer-events-none">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg text-white">
            Магия <span className="text-emerald-500">локации</span>
          </h2>
          <p className="text-lg font-light max-w-2xl mx-auto drop-shadow-md text-slate-300">
            Отель встроен в природный ландшафт горы Машук. Покрутите модель, чтобы оценить масштабы и расстояния до главных точек Пятигорска.
          </p>
          {!weather.loading && (
            <div className="mt-6 inline-flex items-center gap-4 bg-black/10 backdrop-blur-md border border-white/20 px-6 py-2 rounded-full text-sm font-medium shadow-lg text-white">
              <Clock />
              <span className="w-1 h-1 rounded-full bg-white/50"></span>
              <span className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                {weather.temp > 0 ? `+${Math.round(weather.temp)}` : Math.round(weather.temp)}°C
              </span>
              <span className="w-1 h-1 rounded-full bg-white/50"></span>
              <span className="flex items-center gap-1.5">
                {isDay ? "☀️" : "🌙"} {getWeatherDesc(weather.code)}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* 3D Canvas */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 8, 15], fov: 45, far: 5000 }}>
          
          {/* True 3D Sky replacement! */}
          <WeatherEffects code={weather.code} isDay={isDay} />

          <ambientLight intensity={1.5} />
          <Environment preset="city" />

          <Suspense fallback={null}>
            <Center>
              {/* <Resize> automatically scales ANY unknown model to be exactly 10 units wide! */}
              <Resize scale={10}>
                <MashukMountain isDay={isDay} />
              </Resize>
            </Center>
          </Suspense>

          {/* HTML POI MARKERS */}
          {POIS.map((poi, index) => (
            <Html key={poi.id} position={poi.position} center zIndexRange={[100, 0]}>
              <button 
                onClick={(e) => {
                  e.stopPropagation(); // Don't trigger the radar!
                  setActivePOI(poi);
                }}
                className="relative flex items-center justify-center group cursor-pointer"
              >
                {/* The core dot */}
                <div className="w-3 h-3 rounded-full bg-emerald-500 border-[1.5px] border-white shadow-[0_0_10px_rgba(16,185,129,0.5)] z-10 transition-transform duration-500 group-hover:scale-150" />
                
                {/* Ping animation for visibility (Soft & Slow Sonar) */}
                <div 
                  className="absolute w-8 h-8 rounded-full bg-emerald-500/30 animate-sonar" 
                  style={{ animationDelay: `-${index * 0.7}s` }}
                />
                
                {/* Tooltip on hover */}
                <div className="absolute top-6 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 backdrop-blur-md text-white text-xs font-medium whitespace-nowrap px-3 py-1.5 rounded-lg border border-white/20 pointer-events-none drop-shadow-2xl">
                  {poi.title}
                </div>

                {/* SEO & Accessibility: Hidden description in the DOM so search engines can index it! */}
                <span className="sr-only">{poi.description}</span>
              </button>
            </Html>
          ))}

          <OrbitControls 
            target={[0, 1.5, 0]} // Shift camera focus up to keep mountain lower on screen
            enableZoom={true}
            enablePan={false}
            autoRotate={true}
            autoRotateSpeed={0.5}
            minPolarAngle={Math.PI / 6} 
            maxPolarAngle={Math.PI / 2.2} // Restrict camera from going completely horizontal
            minDistance={7} 
            maxDistance={25} 
          />
        </Canvas>
      </div>

      {/* POI MODAL / FLOATING CARD */}
      <div 
        className={`absolute top-6 bottom-6 right-6 w-[calc(100%-3rem)] md:w-[400px] glass-effect bg-slate-900/80 backdrop-blur-2xl border border-white/20 rounded-3xl z-[60] transform transition-all duration-500 ease-out flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)] ${activePOI ? 'translate-x-0 opacity-100' : 'translate-x-[120%] opacity-0 pointer-events-none'}`}
      >
        {activePOI && (
          <div className="flex flex-col h-full overflow-y-auto p-6 scrollbar-hide">
            {/* Close Button */}
            <button 
              onClick={() => setActivePOI(null)}
              className="absolute top-6 right-6 p-2 bg-black/40 hover:bg-black/60 rounded-full transition-colors z-10 backdrop-blur-md border border-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Image Header */}
            <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-8 border border-white/10 shrink-0 shadow-lg">
              <Image 
                src={activePOI.image} 
                alt={activePOI.title} 
                fill 
                className="object-cover hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent pointer-events-none" />
            </div>

            {/* Content */}
            <div className="flex flex-col grow">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                <span className="text-xs font-semibold tracking-wider text-emerald-400 uppercase">Локация</span>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4 tracking-tight">{activePOI.title}</h3>
              <p className="text-slate-300 font-light leading-relaxed text-base mb-8">{activePOI.description}</p>
              
              <div className="mt-auto">
                {activePOI.id === 'hotel' ? (
                  <button className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-medium py-4 rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] transition-all">
                    Забронировать номер
                  </button>
                ) : (
                  <button className="w-full glass-effect border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400 font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Добавить в маршрут
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Top Gradient Mask (Smooth dissolve into the previous section) */}
      <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-slate-900 to-transparent z-10 pointer-events-none"></div>
      
      {/* Bottom Gradient Mask (Smooth dissolve into the footer/next section) */}
      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-slate-900 to-transparent z-10 pointer-events-none"></div>

    </section>
  );
}
